/**
 * Список чатов (история)
 */

import { memo } from 'react';
import { cn } from '@/lib/utils';
import {
  MessageSquare,
  Trash2,
  Loader2,
  Plus,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { Chat } from './types';

interface ChatHistoryProps {
  chats: Chat[];
  currentChatId: string | null;
  isLoading?: boolean;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onNewChat: () => void;
}

/**
 * Форматирует дату для отображения
 */
function formatDate(date: Date): string {
  const now = new Date();
  const d = new Date(date);

  // Сегодня
  if (d.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }

  // Вчера
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return 'Вчера';
  }

  // В этом году
  if (d.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'short',
    }).format(d);
  }

  // Давно
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

/**
 * Элемент списка чатов
 */
const ChatItem = memo(function ChatItem({
  chat,
  isActive,
  onSelect,
  onDelete,
}: {
  chat: Chat;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-muted'
      )}
      onClick={onSelect}
    >
      <MessageSquare
        className={cn(
          'h-4 w-4 flex-shrink-0',
          isActive ? 'text-primary' : 'text-muted-foreground'
        )}
      />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {chat.title || 'Новый чат'}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatDate(new Date(chat.updatedAt))}
        </p>
      </div>

      {/* Кнопка удаления */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity',
              'hover:bg-destructive/10 hover:text-destructive'
            )}
            onClick={e => e.stopPropagation()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить чат?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Чат и вся история сообщений будут удалены.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});

export function ChatHistory({
  chats,
  currentChatId,
  isLoading = false,
  onSelectChat,
  onDeleteChat,
  onNewChat,
}: ChatHistoryProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Заголовок */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">История чатов</h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onNewChat}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Список */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (chats.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Archive className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                Нет сохранённых чатов
              </p>
              <Button
                variant="link"
                size="sm"
                className="mt-1"
                onClick={onNewChat}
              >
                Начать новый чат
              </Button>
            </div>
          ) : (
            chats.map(chat => (
              <ChatItem
                key={chat.id}
                chat={chat}
                isActive={chat.id === currentChatId}
                onSelect={() => onSelectChat(chat.id)}
                onDelete={() => onDeleteChat(chat.id)}
              />
            ))
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
